import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import loadCarouselProducts from "@insite/client-framework/Store/Components/ProductCarousel/Handlers/LoadCarouselProducts";
import {
    getErrorMessage,
    getProductsForProductInfoList,
} from "@insite/client-framework/Store/Components/ProductInfoList/ProductInfoListSelectors";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import SkipNav, { SkipNavStyles } from "@insite/content-library/Components/SkipNav";
import Button, { ButtonIcon, ButtonPresentationProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Tab from "@insite/mobius/Tab";
import TabGroup from "@insite/mobius/TabGroup";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getProp from "@insite/mobius/utilities/getProp";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import CENFBTNewCartProductCarousel from "@tCentro/Components/CENFBTNewCartProductCarousel";
import { ProductCarouselProductStyles } from "@tCentro/Components/CENProductCarouselProduct";
import { TabGroupState } from "@tCentro/Store/CENProductCarouselTabsReducer";
import { COLORS, TYPOGRAPHY } from "@tCentro/theme/design";
import * as React from "react";
import { useState } from "react";
import { connect, DispatchProp, ResolveThunks } from "react-redux";
import { css, ThemeProps, withTheme } from "styled-components";

const enum fields {
    numberOfTabs = "numberOfTabs",
    tabBackgroundColor = "tabBackgroundColor",
    tabColor = "tabColor",
}
interface TabDataModel {
    fields: {
        name: string;
        title: string;
        carouselType: string;
        displayProductsFrom: string;
        selectedCategoryIds: string[];
        relatedProductType: string;
        numberOfProductsToDisplay: number;
        seedWithManuallyAssigned: string;
        maxNumberOfColumns: number;
        showImage: boolean;
        showTitle: boolean;
        showBrandName: boolean;
        showPartNumbers: boolean;
        showPrice: boolean;
        showAddToCart: boolean;
        showAddToList: boolean;
        showProductDescription: boolean;
    };
}
interface OwnProps extends WidgetProps {
    fields: {
        [fields.numberOfTabs]: TabDataModel[];
        [fields.tabBackgroundColor]: string;
        [fields.tabColor]: string;
    };
}

const mapStateToProps = (state: TabGroupState, ownProps: OwnProps) => {
    const productsReducerArr = ownProps.fields.numberOfTabs.reduce((acc: any, item) => {
        const tabName = item.fields.name;
        if (!acc[tabName]) {
            acc[tabName] = [];
        }
        acc[tabName] = getProductsForProductInfoList(state, tabName) || [];
        return acc;
    }, {});

    return {
        products: getProductsForProductInfoList(state, ownProps.id),
        errorMessage: getErrorMessage(state, ownProps.id),
        pageType: getCurrentPage(state).type,
        isRecentlyViewedTabVisible: state.productCarouselTabsReducer.isRecentlyViewedTabVisible,
        isRelatedProductsTabVisible: state.productCarouselTabsReducer.isRelatedProductsTabVisible,
        isComponentsTabVisible: state.productCarouselTabsReducer.isComponentsTabVisible,
        isTopSellerTabVisible: state.productCarouselTabsReducer.isTopSellerTabVisible,
        currentTab: state.productCarouselTabsReducer.currentTab,
        productsReducerArr,
    };
};

const mapDispatchToProps = {
    loadCarouselProducts,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface ProductCarouselStyles {
    errorMessageText?: TypographyPresentationProps;
    titleText?: TypographyPresentationProps;
    skipCarouselButton?: SkipNavStyles;
    mainContainer?: GridContainerProps;
    prevArrowGridItem?: GridItemProps;
    prevArrowButton?: ButtonPresentationProps;
    nextArrowGridItem?: GridItemProps;
    nextArrowButton?: ButtonPresentationProps;
    carouselGridItem?: GridItemProps;
    carouselWrapper?: InjectableCss;
    carouselContainer?: InjectableCss;
    carouselSlidesContainer?: InjectableCss;
    carouselSlide?: InjectableCss;
    carouselSlideInner?: InjectableCss;
    carouselProductStyles?: ProductCarouselProductStyles;
    customproducttilewrapper?: InjectableCss;
    wrapper?: InjectableCss;
    carouselProductWrapper?: InjectableCss;
    titleWeight?: InjectableCss;
}

export const productCarouselStyles: ProductCarouselStyles = {
    errorMessageText: {
        color: "danger",
        css: css`
            margin-top: 20px;
        `,
    },
    titleText: {
        variant: "h2",
        css: css`
            text-align: center;
            margin-bottom: 40px;
            margin-top: 55px;
            color: ${getProp("theme.typography.h2.color")};
            font-weight: 700;
            font-size: 32px !important;
            line-height: 40px;
            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        css`
                            font-weight: 600;
                            font-size: 24px !important;
                            line-height: 32px;
                            margin-bottom: 24px;
                            margin-top: 34px;
                        `,
                        css`
                            font-weight: 600;
                            font-size: 24px !important;
                            line-height: 32px;
                            margin-bottom: 24px;
                            margin-top: 34px;
                        `,
                        null,
                        null,
                        null,
                    ],
                    "max",
                );
            }}
        `,
    },
    mainContainer: {
        gap: 0,
        css: css`
            max-width: 100% !important;
        `,
    },

    customproducttilewrapper: {
        css: css`
            margin: 0 auto;
            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        css`
                            padding-bottom: 30px;
                        `,
                        css`
                            padding-bottom: 30px;
                        `,
                        css`
                            padding-bottom: 30px;
                        `,
                        null,
                        null,
                    ],
                    "max",
                );
            }}
        `,
    },
    prevArrowGridItem: {
        width: 1,
        align: "middle",
        css: css`
            justify-content: flex-start;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        flex-basis: 0%;
                        max-width: 0%;
                        position: relative;
                        right: 25px;
                    `,
                    css`
                        flex-basis: 0%;
                        max-width: 0%;
                        position: relative;
                        right: 25px;
                    `,
                    css`
                        flex-basis: 0%;
                        max-width: 0%;
                        position: relative;
                        right: 25px;
                    `,
                    css`
                        flex-basis: 0%;
                        max-width: 0%;
                        position: relative;
                        right: 25px;
                    `,
                    css`
                        flex-basis: 0%;
                        max-width: 0%;
                        position: relative;
                        right: 25px;
                    `,
                ])}
        `,
    },
    prevArrowButton: {
        variant: "secondary",
        color: "text.main",
        css: css`
            border: 0;
            padding: 0;
        `,
    },
    nextArrowGridItem: {
        width: 1,
        align: "middle",
        css: css`
            justify-content: flex-end;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        flex-basis: 0%;
                        max-width: 0%;
                        position: relative;
                        right: -25px;
                    `,
                    css`
                        flex-basis: 0%;
                        max-width: 0%;
                        position: relative;
                        right: -25px;
                    `,
                    css`
                        flex-basis: 0%;
                        max-width: 0%;
                        position: relative;
                        right: -25px;
                    `,
                    css`
                        flex-basis: 0%;
                        max-width: 0%;
                        position: relative;
                        right: -25px;
                    `,
                    css`
                        flex-basis: 0%;
                        max-width: 0%;
                        position: relative;
                        right: -25px;
                    `,
                ])}
        `,
    },
    nextArrowButton: {
        variant: "secondary",
        color: "text.main",
        css: css`
            border: 0;
            padding: 0;
        `,
    },
    carouselGridItem: {
        width: 10,
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        flex-basis: 100%;
                        max-width: 100%;
                    `,
                    css`
                        flex-basis: 100%;
                        max-width: 100%;
                    `,
                    css`
                        flex-basis: 100%;
                        max-width: 100%;
                    `,
                    css`
                        flex-basis: 100%;
                        max-width: 100%;
                    `,
                    css`
                        flex-basis: 100%;
                        max-width: 100%;
                    `,
                ])}
        `,
    },
    carouselWrapper: {
        css: css`
            width: 100%;
            position: relative;
        `,
    },
    carouselContainer: {
        css: css`
            overflow: hidden;
        `,
    },
    carouselSlidesContainer: {
        css: css`
            display: flex;
        `,
    },
    carouselSlide: {
        css: css`
            padding: 0;
            position: relative;
            flex: 0 0 auto;
            margin-top: 5px;
            @media all and (max-width: 1159px) and (min-width: 992px) {
                width: 50% !important;
            }
            @media all and (max-width: 767px) and (min-width: 576px) {
                width: 100% !important;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        width: 100%;
                    `,
                    css`
                        width: 100%;
                    `,
                    null,
                    null,
                ])}
        `,
    },
    carouselSlideInner: {
        css: css`
            width: 100%;
            height: 100%;
            position: relative;
            padding: 0 2px;
            display: flex;
            flex-direction: column;
            &.recently-viewedTile {
                > div[class^="sc"] {
                    border: 1px solid #d2d0cd;
                    position: relative;
                    padding: 24px 8px 18px;
                    height: 100%;
                    display: flex;
                    flex-direction: column;
                    a[data-test-selector="productImage"] {
                        align-items: flex-start;
                        background: #ffffff;
                        box-shadow:
                            0px 0px 1px rgba(38, 37, 37, 0.56),
                            0px 2px 8px rgba(38, 37, 37, 0.16);
                    }
                    > div[class^="sc"] {
                        [data-test-selector="saleprice-label"] {
                            font-size: ${TYPOGRAPHY.size.base};
                            padding-right: 5px;
                        }
                        [data-test-selector="productPrice_unitNetPrice"] {
                            font-size: ${TYPOGRAPHY.size.base};
                            span {
                                font-size: ${TYPOGRAPHY.size.smaller};
                            }
                        }
                        a[class^="StyledButton"] {
                            > span[class^="TypographyStyle"] {
                                font-size: ${TYPOGRAPHY.size.smaller};
                            }
                        }
                    }
                }
            }
        `,
    },

    carouselProductWrapper: {
        css: css`
            box-shadow: 0px 0px 10px #0000000b;
            border: 1px solid #e6e6e6;
            display: flex;
            flex-direction: column;
            width: 100%;
            height: 100%;
            position: relative;
            padding-top: 15px;
            background: #f2f2f2;
            a[data-test-selector="productImage"] {
                justify-content: center;
                flex-grow: 1;
                ~ div {
                    flex-grow: 0;
                    padding-top: 16px;
                }
                > div {
                    > img {
                        //max-width: 250px;
                        max-height: 250px;
                    }
                }
                > img {
                    //max-width: 250px;
                    max-height: 250px;
                    object-fit: contain;
                    height: auto;
                    aspect-ratio: 1/1;
                }
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        padding-top: 38px;
                    `,
                    css`
                        padding-top: 38px;
                    `,
                    css`
                        padding-top: 38px;
                    `,
                    css`
                        padding-top: 38px;
                    `,
                    null,
                ])}
        `,
    },
    carouselProductStyles: {
        productQuantityOrderedStyles: {
            cssOverrides: {
                formField: css`
                    margin-top: 0;
                    width: 60%;
                    @media all and (max-width: 1400px) and (min-width: 1160px) {
                        input {
                            width: 70px !important;
                        }
                    }
                    input:focus {
                        border-radius: 40px !important;
                    }
                `,
            },
        },
        productAddToCartButtonStyles: {
            css: css`
                width: 100%;
                border-radius: 16px;
                padding: 8px 16px;
                span {
                    font-size: 10px !important;
                }
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        css`
                            span {
                                font-size: 12px;
                            }
                        `,
                        css`
                            span {
                                font-size: 12px;
                            }
                        `,
                        css`
                            span {
                                font-size: 10px;
                            }
                        `,
                        css`
                            span {
                                font-size: 10px;
                            }
                        `,
                        css``,
                    ])}
            `,
            typographyProps: {
                size: 14,
            },
        },
    },
    titleWeight: {
        css: css`
            span {
                font-weight: 600;
            }
        `,
    },
};

const styles = productCarouselStyles;

const CENFBTProductCarousel: React.FC<Props> = ({ id, fields, products, productsReducerArr }) => {
    const wrapper = {
        css: css`
            div[class^="TabGroupWrapper"] {
                ul {
                    z-index: 0;
                    margin-left: 16px;
                    margin-right: 26px;
                    padding: 0;
                    // margin-bottom: 32px;
                    ${({ theme }) => {
                        return breakpointMediaQueries(
                            theme,
                            [
                                null,
                                css`
                                    position: relative;
                                    z-index: 1;
                                    overflow: unset;
                                    flex-direction: column;
                                    width: 100%;
                                `,
                                null,
                                null,
                            ],
                            "max",
                        );
                    }}
                    li {
                        cursor: pointer;
                        text-align: left;
                        font-size: ${TYPOGRAPHY.size.base};
                        letter-spacing: 0px;
                        text-transform: capitalize;
                        padding: 8px 0px 5px;
                        margin-right: 1rem;
                        border-radius: 40px;
                        padding: 4px 16px;
                        border-bottom: none;
                        outline: 0px !important;
                    }
                    li[aria-selected="true"] {
                        font-weight: bold;
                        color: ${fields.tabColor};
                        outline: none;
                        background: ${fields.tabBackgroundColor};
                    }
                    li[aria-selected="false"] {
                        border-bottom: none;
                        color: ${COLORS.background.tag};
                    }
                }
                > div > div[class^="TabContent"] {
                    border-bottom: none;
                    border: none;
                    padding: 0px 16px 0px 16px;
                    ${({ theme }: { theme: BaseTheme }) =>
                        breakpointMediaQueries(
                            theme,
                            [
                                null,
                                css`
                                    padding-left: 0;
                                    padding-right: 0;
                                    .flex-container {
                                        display: block;
                                        .content {
                                            border: 2px solid #bdbbbd;
                                            margin-bottom: 24px;
                                        }
                                        .content.with-background {
                                            border: none;
                                        }
                                    }
                                `,
                                null,
                                null,
                            ],
                            "max",
                        )}
                }
            }
        `,
    };
    const afterCarousel = React.createRef<HTMLSpanElement>();

    const tabNameAndProductsArr =
        Object.values(productsReducerArr) &&
        Object.entries(productsReducerArr).find(([key, value]: any) => value.length > 0);

    const firstDefaultActiveTabObj = tabNameAndProductsArr
        ? { [tabNameAndProductsArr[0]]: tabNameAndProductsArr[1] }
        : {};

    const firstDefaultActiveTabKey = Object.keys(firstDefaultActiveTabObj)[0];

    return (
        <StyledWrapper {...wrapper} className="recently-viewed">
            <SkipNav
                text={translate("Skip Carousel")}
                extendedStyles={styles.skipCarouselButton}
                destination={afterCarousel}
            />
            {fields.numberOfTabs.length > 0 && (
                <TabGroup
                    className="tabsDesktop"
                    data-test-selector="tabMobileDropdown"
                    current={firstDefaultActiveTabKey}
                    defaultValue={firstDefaultActiveTabKey}
                >
                    {fields.numberOfTabs.map((tab, index) => {
                        return (
                            <Tab
                                data-test-selector="tabListData"
                                headline={tab.fields.name}
                                tabKey={tab.fields.name}
                                key={tab.fields.name}
                                {...styles.titleWeight}
                                tabIndex={index}
                            >
                                <CENFBTNewCartProductCarousel
                                    title={tab.fields.title}
                                    itemListTitle={tab.fields.name} 
                                    carouselType={tab.fields.carouselType}
                                    displayProductsFrom={tab.fields.displayProductsFrom}
                                    selectedCategoryIds={tab.fields.selectedCategoryIds}
                                    relatedProductType={tab.fields.relatedProductType}
                                    numberOfProductsToDisplay={tab.fields.numberOfProductsToDisplay}
                                    seedWithManuallyAssigned={tab.fields.seedWithManuallyAssigned}
                                    maxNumberOfColumns={tab.fields.maxNumberOfColumns}
                                    showImage={tab.fields.showImage}
                                    showTitle={tab.fields.showTitle}
                                    showBrandName={tab.fields.showBrandName}
                                    showPartNumbers={tab.fields.showPartNumbers}
                                    showPrice={tab.fields.showPrice}
                                    showAddToCart={tab.fields.showAddToCart}
                                    showAddToList={tab.fields.showAddToList}
                                    showProductDescription={tab.fields.showProductDescription}
                                    id={tab.fields.name}
                                />
                            </Tab>
                        );
                    })}
                </TabGroup>
            )}

            <span ref={afterCarousel} tabIndex={-1} />
        </StyledWrapper>
    );
};

const carouselTypeOptions = [
    { value: "crossSells", displayName: "Cross Sells" },
    { value: "customersAlsoPurchased", displayName: "Customers Also Purchased" },
    { value: "recentlyViewed", displayName: "Recently Viewed" },
    { value: "relatedProducts", displayName: "Related Products" },
    { value: "topSellers", displayName: "Top Sellers" },
];

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(CENFBTProductCarousel),
    definition: {
        group: "Common",
        icon: "Carousel",
        displayName: "CEN New ProductCarousel",
        fieldDefinitions: [
            {
                name: fields.tabBackgroundColor,
                displayName: "Tab BackgroundColor",
                editorTemplate: "TextField",
                defaultValue: "linear-gradient(0deg, rgba(38, 107, 64, 0.08), rgba(38, 107, 64, 0.08)), #ffffff",
                sortOrder: 1,
                fieldType: "General",
            },
            {
                name: fields.tabColor,
                displayName: "Tab Color",
                editorTemplate: "ColorPickerField",
                defaultValue: "#1B4D2E",
                sortOrder: 1,
                fieldType: "General",
            },
            {
                name: fields.numberOfTabs,
                editorTemplate: "ListField",
                getDisplay: (item: HasFields) => {
                    return item.fields.name;
                },
                defaultValue: [],
                fieldType: "General",
                sortOrder: 1,
                fieldDefinitions: [
                    {
                        name: "name",
                        displayName: "Tab Name",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                    {
                        name: "title",
                        displayName: "Title",
                        editorTemplate: "TextField",
                        defaultValue: "",
                        sortOrder: 0,
                    },
                    {
                        name: "carouselType",
                        displayName: "Carousel Type",
                        editorTemplate: "DropDownField",
                        options: carouselTypeOptions,
                        defaultValue: "crossSells",
                        sortOrder: 1,
                    },

                    {
                        name: "displayProductsFrom",
                        displayName: "Display Products From",
                        editorTemplate: "DropDownField",
                        options: [
                            { value: "allCategories", displayName: "All Categories" },
                            { value: "selectedCategories", displayName: "Selected Categories" },
                            { value: "customerSegments", displayName: "Customer Segments" },
                        ],
                        defaultValue: "allCategories",
                        isVisible: item => item.fields["carouselType"] === "topSellers",
                        sortOrder: 2,
                    },
                    {
                        name: "selectedCategoryIds",
                        displayName: "Selected Categories",
                        editorTemplate: "CategoriesField",
                        defaultValue: [],
                        isVisible: item =>
                            item.fields["carouselType"] === "topSellers" &&
                            item.fields["displayProductsFrom"] === "selectedCategories",
                        sortOrder: 3,
                    },
                    {
                        name: "relatedProductType",
                        displayName: "Related Product Type",
                        editorTemplate: "SystemListDropDownField",
                        systemListName: "ProductRelationship",
                        defaultValue: "",
                        isVisible: item => item.fields["carouselType"] === "relatedProducts",
                        sortOrder: 4,
                    },
                    {
                        name: "numberOfProductsToDisplay",
                        displayName: "Number Of Products To Display",
                        editorTemplate: "IntegerField",
                        min: 1,
                        max: 20,
                        defaultValue: 10,
                        isVisible: item => item.fields["carouselType"] === "customersAlsoPurchased",
                        sortOrder: 5,
                    },
                    {
                        name: "seedWithManuallyAssigned",
                        displayName: "Seed With Manually Assigned",
                        editorTemplate: "SystemListDropDownField",
                        systemListName: "ProductRelationship",
                        defaultValue: "",
                        isVisible: item => item.fields["carouselType"] === "customersAlsoPurchased",
                        sortOrder: 6,
                    },
                    {
                        name: "maxNumberOfColumns",
                        displayName: "Max Number Of Columns",
                        editorTemplate: "IntegerField",
                        min: 1,
                        max: 6,
                        defaultValue: 6,
                        sortOrder: 7,
                    },
                    {
                        name: "showImage",
                        displayName: "Image",
                        editorTemplate: "CheckboxField",
                        defaultValue: true,
                        isEnabled: () => false,
                        sortOrder: 8,
                    },
                    {
                        name: "showTitle",
                        displayName: "Title",
                        editorTemplate: "CheckboxField",
                        defaultValue: true,
                        isEnabled: () => false,
                        sortOrder: 9,
                    },
                    {
                        name: "showBrandName",
                        displayName: "Brand Name",
                        editorTemplate: "CheckboxField",
                        defaultValue: true,
                        sortOrder: 10,
                    },
                    {
                        name: "showPartNumbers",
                        displayName: "Part Numbers",
                        editorTemplate: "CheckboxField",
                        defaultValue: true,
                        sortOrder: 11,
                    },
                    {
                        name: "showPrice",
                        displayName: "Price",
                        editorTemplate: "CheckboxField",
                        defaultValue: true,
                        sortOrder: 12,
                    },
                    {
                        name: "showAddToCart",
                        displayName: "Add to Cart",
                        editorTemplate: "CheckboxField",
                        defaultValue: true,
                        sortOrder: 13,
                    },
                    {
                        name: "showAddToList",
                        displayName: "Show Wishlist",
                        editorTemplate: "CheckboxField",
                        defaultValue: true,
                        sortOrder: 14,
                    },
                    {
                        name: "showProductDescription",
                        displayName: "Show Product Description",
                        editorTemplate: "CheckboxField",
                        defaultValue: true,
                        sortOrder: 15,
                    },
                ],
            },
        ],
    },
};

export default widgetModule;
